"use client";

import * as React from "react";
import { format, startOfDay } from "date-fns";
import { useTranslation } from "react-i18next";
import type { DateRange as RDPDateRange } from "react-day-picker";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/overlays/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/primitives/button";
import { DATE_FORMATS } from "@/lib/date/config";

// =============================================================================
// Types
// =============================================================================

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

export interface DateRangePickerProps {
  /** Current date range value */
  value?: DateRange;
  /** Callback when date range changes */
  onChange?: (range: DateRange) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Additional class name for the trigger wrapper */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Format for displaying dates */
  dateFormat?: string;
  /** Size variant – mirrors DateTimeInput sizes */
  size?: "sm" | "md" | "lg";
  /** Trigger style: "input" (default) or "button" (outline button with calendar icon) */
  triggerVariant?: "input" | "button";
  /** Number of months to show in the calendar (e.g. 2 for range picker) */
  numberOfMonths?: number;
}

// =============================================================================
// Main Component
// =============================================================================

function DateRangePicker({
  value,
  onChange,
  placeholder = "dd-mm-yyyy",
  className,
  disabled = false,
  dateFormat = DATE_FORMATS.short,
  size = "md",
  triggerVariant = "input",
  numberOfMonths = 1,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const { t } = useTranslation("common");

  // Map external value to react-day-picker's DateRange
  const selected: RDPDateRange | undefined =
    value?.from || value?.to ? { from: value?.from, to: value?.to } : undefined;

  const handleSelect = (range: RDPDateRange | undefined) => {
    if (!range?.from) {
      onChange?.({ from: undefined, to: undefined });
      return;
    }
    // First click = from only; same-day selection = treat as from only, keep calendar open
    const fromOnly =
      !range.to ||
      startOfDay(range.from).getTime() === startOfDay(range.to).getTime();
    if (fromOnly) {
      onChange?.({ from: range.from, to: undefined });
      return;
    }
    // Second date selected (different from from) → set both and close
    onChange?.({ from: range.from, to: range.to });
    setIsOpen(false);
  };

  const handleToday = () => {
    const today = startOfDay(new Date());
    onChange?.({ from: today, to: today });
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange?.({ from: undefined, to: undefined });
  };

  // Display value
  const displayValue = React.useMemo(() => {
    if (value?.from && value?.to) {
      return `${format(value.from, dateFormat)} - ${format(value.to, dateFormat)}`;
    }
    if (value?.from) {
      return format(value.from, dateFormat);
    }
    return "";
  }, [value?.from, value?.to, dateFormat]);

  // Size tokens – identical to DateTimeInput
  const sizeClasses = {
    sm: {
      input: "py-(--input-sm-padding-y) body-xs-regular",
      paddingL: "pl-(--input-sm-padding-x)",
      paddingR:
        "pr-[calc(var(--input-sm-padding-x)+var(--input-sm-icon-size)+var(--input-sm-gap))]",
      icon: "size-[var(--input-sm-icon-size)]",
      iconRight: "right-(--input-sm-padding-x)",
    },
    md: {
      input: "py-(--input-md-padding-y) body-sm-regular",
      paddingL: "pl-(--input-md-padding-x)",
      paddingR:
        "pr-[calc(var(--input-md-padding-x)+var(--input-md-icon-size)+var(--input-md-gap))]",
      icon: "size-[var(--input-md-icon-size)]",
      iconRight: "right-(--input-md-padding-x)",
    },
    lg: {
      input: "py-(--input-lg-padding-y) body-md-regular",
      paddingL: "pl-(--input-lg-padding-x)",
      paddingR:
        "pr-[calc(var(--input-lg-padding-x)+var(--input-lg-icon-size)+var(--input-lg-gap))]",
      icon: "size-[var(--input-lg-icon-size)]",
      iconRight: "right-(--input-lg-padding-x)",
    },
  };

  const s = sizeClasses[size];

  const triggerClassName = cn(
    // Base – same as DateTimeInput
    "relative w-full rounded-(--input-radius) border transition-colors text-left",
    "shadow-(--input-shadow)",
    "outline-1 -outline-offset-1 outline-transparent",
    s.input,
    s.paddingL,
    s.paddingR,
    // Default state
    "bg-(--input-bg) border-(--input-border) text-(--input-text)",
    "placeholder:text-(--input-text-placeholder)",
    // Hover
    !disabled && "hover:border-(--input-border-hover)",
    // Focus
    "focus:outline-[1.5px] focus:-outline-offset-[1.5px] focus:outline-(--input-border-focus)",
    // Disabled
    disabled &&
      "bg-(--input-bg-disabled) text-(--input-text-disabled) cursor-not-allowed pointer-events-none opacity-50",
    className
  );

  const isButtonTrigger = triggerVariant === "button";

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {isButtonTrigger ? (
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              "justify-start px-2.5 font-normal w-full h-[38px]",
              className
            )}
          >
            <CalendarIcon className="size-4 shrink-0 text-text-tertiary" />
            {displayValue ? (
              <span className="truncate">{displayValue}</span>
            ) : (
              <span className="text-text-tertiary">{placeholder}</span>
            )}
          </Button>
        ) : (
          <button type="button" disabled={disabled} className={triggerClassName}>
            <span
              className={cn(
                displayValue
                  ? "text-(--input-text)"
                  : "text-(--input-text-placeholder)"
              )}
            >
              {displayValue || placeholder}
            </span>
            <span
              className={cn(
                "absolute top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none",
                s.iconRight,
                s.icon,
                "text-(--input-icon)"
              )}
            >
              <i className="pi pi-calendar" aria-hidden="true" />
            </span>
          </button>
        )}
      </PopoverTrigger>

      <PopoverContent className="w-auto p-0 bg-white rounded-lg overflow-hidden" align="start">
        <Calendar
          mode="range"
          defaultMonth={value?.from}
          selected={selected}
          onSelect={handleSelect}
          numberOfMonths={numberOfMonths}
          captionLayout="dropdown"
          startMonth={new Date(2020, 0)}
          endMonth={new Date(2040, 11)}
          className="rounded-none border-0 shadow-none"
        />
        <div className="flex gap-2 p-2 border-t border-border-secondary bg-white">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={handleClear}
          >
            {t("clear")}
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            className="flex-1"
            onClick={handleToday}
          >
            {t("today")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

DateRangePicker.displayName = "DateRangePicker";

export { DateRangePicker };
